import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  autoClose?: boolean; // Onay verildiğinde modal'ı otomatik kapat
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Tamam',
  cancelText = 'İptal',
  onConfirm,
  onCancel,
  autoClose = false
}) => {
  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  // Modal geri düğmesi handler
  useModalBackHandler({ 
    isOpen, 
    onClose: onCancel, 
    enabled: true
  });

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
              className="relative transform transition-transform overflow-hidden rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-lg"
            >
              <div className="p-8">
                <div className="text-center">
                  <svg className="mx-auto h-14 w-14 text-yellow-500 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  <motion.h3 
                    initial={{ y: 10, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="text-xl font-semibold text-gray-900 dark:text-white mb-3"
                  >
                    {title}
                  </motion.h3>
                  <motion.p 
                    initial={{ y: 10, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="text-gray-600 dark:text-gray-300 mb-8 whitespace-pre-line"
                  >
                    {message}
                  </motion.p>
                  <div className="flex justify-center space-x-4">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      <div className="rounded-lg border border-white dark:border-gray-700 px-5 py-2 text-base font-medium text-gray-700 dark:text-gray-300">
                        {cancelText}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (autoClose) {
                          onCancel(); // Modal'ı hemen kapat
                        }
                        onConfirm(); // İşlemi başlat
                      }}
                      className="px-5 py-2.5 text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] font-medium relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                    >
                      {confirmText}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationDialog; 